/**
 * src/BackupUtilityPage.js
 *
 * VISINVOKE Backup Utility — Dashboard DISCOVERY page.
 *
 * The Dashboard is the discovery layer; the RUNTIME is the DCC-hosted product window
 * (visinvoke-desktop-app: src/backup/BackupSurface.jsx + src-tauri/src/backup.rs), which is the one
 * authoritative Backup Utility implementation.
 *
 * This page previously rendered a full operational backup console — projects, locations, history,
 * storage bars, run/restore modals — entirely from a hardcoded `DATA` constant, with the note
 * "replace with API responses when backend is ready". None of it was connected to anything: no file
 * pickers, no API calls, no filesystem access. It presented simulated operational data as though it
 * were a live backup system, and it advertised a Restore capability the product does not implement.
 *
 * That is removed. What remains describes only what the real engine actually does. Nothing on this
 * page claims a capability the runtime does not have, and nothing here shows invented run data.
 */
import React, { useEffect } from 'react';
import { glassSurface, viGlassFill, viGlassBorder, viGlassShadow } from './config/glassTheme';
import { recordProductOpened } from './productActivity';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

// ── Palette (unchanged) ────────────────────────────────────────────────────
const C = {
  bg:        '#0d1117',
  surface:   '#0f172a',
  surfaceHi: '#1e293b',
  border:    'rgba(255,255,255,0.08)',
  borderHi:  'rgba(255,255,255,0.14)',
  text:      '#f1f5f9',
  textMid:   '#94a3b8',
  textFaint: 'rgba(255,255,255,0.4)',
  blue:      '#3b82f6',
  green:     '#4ade80',
};

// Each entry describes behaviour implemented in src-tauri/src/backup.rs. Nothing aspirational.
const WHAT_IT_DOES = [
  {
    title: 'Project-based backups',
    body: 'Create a backup project: pick a source folder, then choose one or more backup locations to copy it to.',
  },
  {
    title: 'Copies what changed',
    body: 'New files are copied and changed files are updated. Files that already match at the destination are skipped, so repeat runs only move what actually differs.',
  },
  {
    title: 'Never deletes at the destination',
    body: 'Files that exist only at the backup location are left alone. This is an additive backup, not a mirror.',
  },
  {
    title: 'Folder structure preserved',
    body: 'Your folder layout is reproduced under the backup location, so the copy is browsable exactly like the original.',
  },
  {
    title: 'Folder exclusions',
    body: 'Exclude folders by name at any depth. node_modules, .git and __pycache__ are always excluded.',
  },
  {
    title: 'Five location types',
    body: 'Local Drive, External Drive, Network Location, NAS / Mapped Drive and Custom Folder — each shown as online or offline before a run.',
  },
  {
    title: 'Live progress you can cancel',
    body: 'Per-file progress with running copied / updated / skipped / failed counts and data transferred. A run can be cancelled while it is in progress.',
  },
  {
    title: 'History and reports',
    body: 'Every run is recorded with its duration, result and counts, and writes a report file you can open from the utility.',
  },
];

const Card = ({ children, style }) => (
  <div style={{
    background: viGlassFill('#123A8A', C.blue, '#63A6FF'), border: viGlassBorder('#63A6FF', 1.5), boxShadow: viGlassShadow('#123A8A', C.blue, '#63A6FF', false),
    borderRadius: 14, padding: 22, ...style,
  }}>{children}</div>
);

export default function BackupUtilityPage() {
  // Universal product activity. This page IS the launch surface for utility_backup_utility, so mounting it
  // is the only honest evidence that the customer opened the product. Once per browser
  // session; the payload carries no identity, no timestamp and no customer content, and a
  // failure is swallowed rather than allowed to break the product.
  useEffect(() => { recordProductOpened('utility_backup_utility', 'web'); }, []);

  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div style={{ minHeight: '100vh', background: C.bg,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      {/* Header — unchanged chrome */}
      <div style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1a2744 60%, #1e3a8a 100%)',
                    borderBottom: `1px solid ${C.borderHi}` }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '18px 48px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <button onClick={() => navigate('/dashboard/utilities')}
              style={{ ...glassSurface('#334155', '#64748B', '#94A3B8'), borderRadius: 8, padding: '6px 14px', fontSize: 13,
                fontWeight: 600, cursor: 'pointer' }}>
              ← Utilities
            </button>
            <span style={{ color: C.textFaint, fontSize: 13 }}>›</span>
            <span style={{ color: C.textFaint, fontSize: 13, fontWeight: 600 }}>Backup Utility</span>
            <span style={{ fontSize: 11, fontWeight: 700, ...glassSurface('#0E7A50', C.green, '#6EE7B7'), borderRadius: 5, padding: '2px 8px',
              textTransform: 'uppercase', letterSpacing: '0.07em' }}>Free</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end',
                        justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                <span style={{ fontSize: 26 }}>💾</span>
                <h1 style={{ margin: 0, fontSize: 27, fontWeight: 900, color: C.text,
                             letterSpacing: '-0.4px' }}>
                  VISINVOKE Backup Utility
                </h1>
              </div>
              <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                Simple project-based backup management for VISINVOKE users.
              </div>
            </div>
            {user && <div style={{ fontSize: 13, color: C.textMid, fontWeight: 600 }}>{user.email}</div>}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '32px 48px 80px' }}>

        {/* Where it runs */}
        <Card style={{ marginBottom: 28, borderColor: C.borderHi }}>
          <div style={{ fontSize: 11, color: C.textMid, textTransform: 'uppercase',
                        letterSpacing: '.12em', marginBottom: 8 }}>Where it runs</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 8 }}>
            Backup Utility runs in the VISINVOKE Desktop Command Centre
          </div>
          <div style={{ fontSize: 14, color: C.textMid, lineHeight: 1.65, maxWidth: 760 }}>
            Backing up files needs direct access to your drives, so the utility runs on your desktop
            rather than in the browser. Open the Desktop Command Centre, find Backup Utility under
            Utilities, and choose Launch — it opens in its own window. It is included free with your
            VISINVOKE account; there is nothing extra to buy and nothing extra to install.
          </div>
        </Card>

        {/* What it does */}
        <h2 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '.12em',
                     color: C.textMid, margin: '0 0 16px', fontWeight: 700 }}>
          What it does
        </h2>
        <div style={{ display: 'grid', gap: 16,
                      gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))' }}>
          {WHAT_IT_DOES.map((f) => (
            <Card key={f.title}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 8 }}>
                {f.title}
              </div>
              <div style={{ fontSize: 13.5, color: C.textMid, lineHeight: 1.6 }}>{f.body}</div>
            </Card>
          ))}
        </div>

        {/* What it does not do — stated plainly rather than implied by omission */}
        <Card style={{ marginTop: 28 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 10 }}>
            What it does not do
          </div>
          <div style={{ fontSize: 13.5, color: C.textMid, lineHeight: 1.7, maxWidth: 760 }}>
            Backup Utility copies files to locations you choose. It does not run backups on a
            schedule, manage retention or versions, restore files back to their source, verify copies
            with checksums, encrypt backups, or upload to cloud storage. If you need any of those,
            it is not the right tool yet.
          </div>
        </Card>

      </div>
    </div>
  );
}
